'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Moon, Star, Copy, Check } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const DEMO_CREDENTIALS = { email: 'mae@decretos.app', password: 'noite2026' };

export default function LoginForm() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'register' | 'confirm-email'>('login');
  const [registeredEmail, setRegisteredEmail] = useState('');

  const loginForm = useForm<LoginFormData>({ defaultValues: { remember: false } });
  const registerForm = useForm<RegisterFormData>();

  const onLogin = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Bem-vinda de volta! ✨');
      router.push('/dashboard');
    } catch (err: any) {
      toast.error(err?.message || 'Credenciais inválidas. Verifique e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'As senhas não coincidem.' });
      return;
    }
    setLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      setRegisteredEmail(data.email);
      setMode('confirm-email');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (field: 'email' | 'password') => {
    const value = field === 'email' ? DEMO_CREDENTIALS.email : DEMO_CREDENTIALS.password;
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseDemoCredentials = () => {
    loginForm.setValue('email', DEMO_CREDENTIALS.email);
    loginForm.setValue('password', DEMO_CREDENTIALS.password);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #6B5CE7 0%, #7C6AF7 40%, #9B8BFF 100%)' }}>
        <div className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <div
              key={`star-bg-${i}`}
              className="absolute rounded-full bg-white"
              style={{
                width: `${(i + 1) * 3}px`,
                height: `${(i + 1) * 3}px`,
                top: `${15 + i * 13}%`,
                left: `${10 + i * 12}%`,
                opacity: 0.3 + i * 0.1,
              }}
            />
          ))}
        </div>
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={44} />
            <span className="text-white font-semibold text-xl">Decretos Dashboard</span>
          </div>
          <div className="mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
              <Moon size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white leading-tight mb-4">
              Rotinas de afirmação<br />para noites tranquilas
            </h1>
            <p className="text-white/75 text-base leading-relaxed max-w-sm">
              Crie decretos personalizados, monte rotinas e acompanhe a consistência do seu ritual noturno com seu filho.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { icon: Star, text: '25 decretos prontos em português' },
              { icon: Moon, text: 'Modo foco para leitura sem distrações' },
              { icon: Star, text: 'Streak e histórico de sessões' },
            ].map((item, i) => {
              const ItemIcon = item.icon;
              return (
                <div key={`feature-${i}`} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <ItemIcon size={14} className="text-white" />
                  </div>
                  <span className="text-white/80 text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F7F6F3]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <AppLogo size={36} />
            <span className="font-semibold text-[#0F172A]">Decretos Dashboard</span>
          </div>

          <div className="card p-8">
            {mode === 'confirm-email' ? (
              <>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#F0EEFF] flex items-center justify-center mb-5">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7C6AF7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Confirme seu e-mail</h2>
                  <p className="text-sm text-[#64748B] mb-1 leading-relaxed">
                    Enviamos um link de confirmação para:
                  </p>
                  <p className="text-sm font-semibold text-[#7C6AF7] mb-5 break-all">{registeredEmail}</p>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-6">
                    Clique no link do e-mail para ativar sua conta e acessar o app. Verifique também a pasta de spam.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); loginForm.setValue('email', registeredEmail); }}
                    className="btn-primary w-full justify-center py-3 text-base"
                  >
                    Já confirmei, ir para login
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('register'); registerForm.reset(); setRegisteredEmail(''); }}
                    className="mt-3 text-sm text-[#94A3B8] hover:text-[#64748B] transition-colors"
                  >
                    Usar outro e-mail
                  </button>
                </div>
              </>
            ) : mode === 'login' ? (
              <>
                <h2 className="text-xl font-semibold text-[#0F172A] mb-1">Entrar</h2>
                <p className="text-sm text-[#64748B] mb-6">Acesse seu painel de decretos</p>

                <form onSubmit={loginForm.handleSubmit(onLogin)} noValidate className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      E-mail
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="mae@exemplo.com"
                      className={`input-field ${loginForm.formState.errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                      {...loginForm.register('email', {
                        required: 'E-mail é obrigatório.',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido.' },
                      })}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      Senha
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        className={`input-field pr-10 ${loginForm.formState.errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                        {...loginForm.register('password', {
                          required: 'Senha é obrigatória.',
                          minLength: { value: 6, message: 'Mínimo de 6 caracteres.' },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] transition-colors"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="mt-1 text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="remember"
                      type="checkbox"
                      className="w-4 h-4 rounded border-[#D1D5DB] text-[#7C6AF7] focus:ring-[#7C6AF7]/30"
                      {...loginForm.register('remember')}
                    />
                    <label htmlFor="remember" className="text-sm text-[#64748B]">Lembrar de mim</label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center py-3 text-base"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Entrando...
                      </span>
                    ) : 'Entrar'}
                  </button>
                </form>

                {/* Demo credentials box */}
                <div className="mt-5 rounded-xl border border-[#E8E7F0] bg-[#F8F7FF] p-3.5">
                  <p className="text-xs font-medium text-[#7C6AF7] mb-2.5 uppercase tracking-wider">Conta demo</p>
                  <div className="space-y-2">
                    {(['email', 'password'] as const).map((field) => (
                      <div key={`cred-${field}`} className="flex items-center justify-between gap-2">
                        <div>
                          <span className="text-xs text-[#94A3B8] block">{field === 'email' ? 'E-mail' : 'Senha'}</span>
                          <span className="text-xs font-mono text-[#0F172A]">
                            {field === 'email' ? DEMO_CREDENTIALS.email : DEMO_CREDENTIALS.password}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopy(field)}
                          className="p-1.5 rounded-lg hover:bg-[#E8E6FF] transition-colors text-[#7C6AF7]"
                          aria-label={`Copiar ${field}`}
                        >
                          {copiedField === field ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleUseDemoCredentials}
                    className="mt-2.5 w-full text-xs font-medium text-[#7C6AF7] hover:text-[#6B5CE7] transition-colors py-1 rounded-lg hover:bg-[#E8E6FF]"
                  >
                    Preencher automaticamente →
                  </button>
                </div>

                {/* Switch to register */}
                <p className="mt-5 text-center text-sm text-[#64748B]">
                  Não tem conta?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('register'); registerForm.reset(); }}
                    className="font-medium text-[#7C6AF7] hover:text-[#6B5CE7] transition-colors"
                  >
                    Criar conta
                  </button>
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-[#0F172A] mb-1">Criar conta</h2>
                <p className="text-sm text-[#64748B] mb-6">Seus dados ficam privados e seguros</p>

                <form onSubmit={registerForm.handleSubmit(onRegister)} noValidate className="space-y-4">
                  <div>
                    <label htmlFor="reg-name" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      Nome
                    </label>
                    <input
                      id="reg-name"
                      type="text"
                      autoComplete="name"
                      placeholder="Seu nome"
                      className={`input-field ${registerForm.formState.errors.name ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                      {...registerForm.register('name', { required: 'Nome é obrigatório.' })}
                    />
                    {registerForm.formState.errors.name && (
                      <p className="mt-1 text-xs text-red-500">{registerForm.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="reg-email" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      E-mail
                    </label>
                    <input
                      id="reg-email"
                      type="email"
                      autoComplete="email"
                      placeholder="mae@exemplo.com"
                      className={`input-field ${registerForm.formState.errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                      {...registerForm.register('email', {
                        required: 'E-mail é obrigatório.',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido.' },
                      })}
                    />
                    {registerForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-red-500">{registerForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="reg-password" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      Senha
                    </label>
                    <div className="relative">
                      <input
                        id="reg-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Mínimo 6 caracteres"
                        className={`input-field pr-10 ${registerForm.formState.errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                        {...registerForm.register('password', {
                          required: 'Senha é obrigatória.',
                          minLength: { value: 6, message: 'Mínimo de 6 caracteres.' },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] transition-colors"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {registerForm.formState.errors.password && (
                      <p className="mt-1 text-xs text-red-500">{registerForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="reg-confirm" className="block text-sm font-medium text-[#0F172A] mb-1.5">
                      Confirmar senha
                    </label>
                    <div className="relative">
                      <input
                        id="reg-confirm"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Repita a senha"
                        className={`input-field pr-10 ${registerForm.formState.errors.confirmPassword ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                        {...registerForm.register('confirmPassword', { required: 'Confirmação é obrigatória.' })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] transition-colors"
                        aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {registerForm.formState.errors.confirmPassword && (
                      <p className="mt-1 text-xs text-red-500">{registerForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center py-3 text-base"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Criando conta...
                      </span>
                    ) : 'Criar conta'}
                  </button>
                </form>

                {/* Switch back to login */}
                <p className="mt-5 text-center text-sm text-[#64748B]">
                  Já tem conta?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); loginForm.reset(); }}
                    className="font-medium text-[#7C6AF7] hover:text-[#6B5CE7] transition-colors"
                  >
                    Entrar
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}